import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from '@tanstack/react-router'
import clsx from 'clsx'
import { FileDiff, Layers, Settings } from 'lucide-react'
import { motion } from 'motion/react'

import { MenuItem } from './elements'
import { useJumpToMainNavigation, useLandscapeBottomNavLayout, usePrefersReducedMotion } from './hooks'
import type { BottomMenuProps } from './types'
import { pathnameToActiveIndex } from './utils'

const selectionBgSpring = {
  type: 'spring' as const,
  stiffness: 400,
  damping: 17,
  bounce: 0.34,
}

const selectionBgTween = {
  type: 'tween' as const,
  duration: 0.18,
  ease: 'easeOut' as const,
}

export const BottomMenu = ({ paths = { cards: '/cards', list: '/list', settings: '/settings' } }: BottomMenuProps) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const activeIndex = pathnameToActiveIndex(pathname, paths)
  const isLandscapeNav = useLandscapeBottomNavLayout()
  const prefersReducedMotion = usePrefersReducedMotion()
  const selectionTransition = prefersReducedMotion ? selectionBgTween : selectionBgSpring
  const firstLinkRef = useRef<HTMLAnchorElement>(null)

  useJumpToMainNavigation(firstLinkRef)

  return (
    <div className="shrink-0 px-4 pb-3 sm-landscape:flex sm-landscape:min-h-0 sm-landscape:min-w-0 sm-landscape:flex-row-reverse sm-landscape:pt-3">
      <nav
        aria-keyshortcuts="Alt+M"
        aria-label={t('Main navigation')}
        className="dark:backdrop-saturate-125 relative flex min-w-0 overflow-hidden rounded-full bg-white/25 p-1.5 shadow-lg shadow-black/[0.08] ring-1 ring-black/[0.05] backdrop-blur-2xl backdrop-saturate-150 sm-landscape:min-h-0 sm-landscape:min-w-0 sm-landscape:flex-1 sm-landscape:flex-col dark:border-white/[0.14] dark:bg-slate-950/35 dark:shadow-black/40 dark:ring-white/[0.08] dark:backdrop-blur-2xl"
        title={t('Main navigation keyboard shortcut')}
      >
        <motion.div
          animate={{
            x: isLandscapeNav ? 0 : `${activeIndex * 100}%`,
            y: isLandscapeNav ? `${activeIndex * 100}%` : 0,
          }}
          className={clsx(
            'pointer-events-none absolute z-0 rounded-full bg-slate-900/[0.06] ring-1 ring-black/[0.04] dark:bg-white/[0.09] dark:ring-white/[0.06]',
            isLandscapeNav
              ? // 6px - prevent pill from touching the outer rounded glass edge, 3 - the count of bottom menu items
                'left-1.5 right-1.5 h-[calc((100%-2*6px)/3)]'
              : 'bottom-1.5 top-1.5 w-[calc((100%-2*6px)/3)]',
          )}
          initial={false}
          transition={selectionTransition}
        />
        <MenuItem ref={firstLinkRef} Icon={Layers} label={t('cards')} to={paths.cards} />
        <MenuItem Icon={FileDiff} label={t('list')} to={paths.list} />
        <MenuItem Icon={Settings} label={t('settings')} to={paths.settings} />
      </nav>
    </div>
  )
}
